// Entwickler (REST-API-Doku mit Ausprobieren, API-Schlüssel) und Verwaltung › System
// (Engine-Gesundheit, Protokoll, Speicher, Reparaturen, Datenbank-Konsole, Sicherungen, Sitzungen).

import { useState } from "react";
import { api, fmtTime, toast, useLoad } from "../api";
import { Empty, Field, HowTo, PageHead, useMe } from "../ui";

const ENDPOINTS: [string, string, string][] = [
  ["GET", "/api/v1/stations", "Alle Sender"],
  ["GET", "/api/v1/stations/{sender}", "Ein Sender (Slug oder ID)"],
  ["GET", "/api/v1/stations/{sender}/now", "Läuft gerade, als Nächstes, live/automatisch"],
  ["GET", "/api/v1/stations/{sender}/history?limit=20", "Zuletzt gespielte Songs"],
  ["GET", "/api/v1/stations/{sender}/schedule", "Programme"],
  ["GET", "/api/v1/stations/{sender}/status", "Status und Hörer"],
  ["POST", "/api/v1/stations/{sender}/skip", "Überspringen (Schlüssel mit „control“)"],
];

export function Developer() {
  const me = useMe();
  const admin = me?.role === "admin";
  const { data: st } = useLoad<any>("/api/station");
  const { data: keys, reload } = useLoad<any[]>(admin ? "/api/keys" : null);
  const [out, setOut] = useState<string>("");
  const [name, setName] = useState("");
  const [control, setControl] = useState(false);
  const tryIt = async (path: string) => {
    const r = await fetch(path.replace("{sender}", st?.slug ?? "main"));
    setOut(`${r.status}\n${JSON.stringify(await r.json().catch(() => null), null, 2)}`);
  };
  return (
    <div>
      <PageHead title="Entwickler" sub="REST-API für Website, App und Partner" />
      <HowTo text="Lesen ist offen (für Websites, auch von fremden Domains). Steuern braucht einen API-Schlüssel." chapter="entwickler" />
      <div className="grid g2">
        <div className="card">
          <h2>Schnittstellen</h2>
          <table className="list"><tbody>{ENDPOINTS.map(([m, p, d]) => (
            <tr key={p}><td><span className="badge">{m}</span></td><td className="mono small">{p}</td><td className="small muted">{d}</td>
              <td>{m === "GET" && <button className="btn small" onClick={() => tryIt(p)}>Ausprobieren</button>}</td></tr>))}</tbody></table>
          <p className="small muted">Schlüssel als Kopfzeile <span className="mono">Authorization: Bearer rck_…</span>. Webhooks findest du im Senderbereich.</p>
          {out && <pre className="mono" style={{ background: "#fbfaf6", padding: 10, borderRadius: 8, maxHeight: 320, overflow: "auto" }}>{out}</pre>}
        </div>
        {admin && (
          <div className="card">
            <h2>API-Schlüssel</h2>
            {(keys ?? []).map((k) => (
              <div key={k.id} className="row" style={{ padding: "4px 0", borderBottom: "1px solid var(--line)", opacity: k.revoked ? 0.5 : 1 }}>
                <b>{k.name}</b><span className="mono small">{k.prefix}…</span><span className="small muted">{k.scopes.join(", ")} · zuletzt {k.last_used ? fmtTime(k.last_used) : "nie"}</span><span className="spacer" />
                {!k.revoked && <button className="btn small danger" onClick={async () => { await api(`/api/keys/${k.id}`, { method: "DELETE" }); reload(); }}>Widerrufen</button>}
              </div>
            ))}
            {!keys?.length && <Empty>Keine Schlüssel.</Empty>}
            <div className="row" style={{ marginTop: 8 }}>
              <input type="text" placeholder="Name, z. B. Website" value={name} onChange={(e) => setName(e.target.value)} />
              <label className="row small"><input type="checkbox" checked={control} onChange={(e) => setControl(e.target.checked)} /> darf steuern</label>
              <button className="btn" disabled={!name.trim()} onClick={async () => { const r = await api("/api/keys", { body: { name, scopes: control ? ["read", "control"] : ["read"] } }); setName(""); reload(); window.prompt("Neuer Schlüssel – wird nur jetzt angezeigt:", r.key); }}>Erzeugen</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function SystemPanel() {
  const { data: h, reload } = useLoad<any>("/api/admin/health");
  const { data: storage } = useLoad<any>("/api/admin/storage");
  const { data: backups, reload: reloadB } = useLoad<any[]>("/api/admin/backups");
  const { data: sessions, reload: reloadS } = useLoad<any[]>("/api/admin/sessions");
  const [unit, setUnit] = useState("engine");
  const { data: logs, reload: reloadL } = useLoad<any>(`/api/admin/logs?unit=${unit}&lines=200`, [unit]);
  const [sql, setSql] = useState("SELECT title, artist, started_at FROM play_log ORDER BY started_at DESC LIMIT 20");
  const [res, setRes] = useState<any | null>(null);
  const repair = async (what: string, label: string) => {
    try {
      const r = await api("/api/admin/repair", { body: { what } });
      toast(`${label}: ${JSON.stringify(r).replace(/[{}"]/g, "").replace(/ok:true,?/, "")}`);
      reload();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const gb = (b: number) => `${(b / 1e9).toLocaleString("de-DE", { maximumFractionDigits: 2 })} GB`;
  return (
    <div className="stack">
      <div className="card">
        <div className="row"><h2 style={{ margin: 0 }}>Engine-Gesundheit</h2><span className="spacer" /><button className="btn small" onClick={reload}>Aktualisieren</button>
          <button className="btn small danger" onClick={async () => { if (window.confirm("Harter Reset: Die Engine startet komplett neu (Hörer des Test-Mounts kurz in der Pausenschleife).")) { try { await api("/api/admin/hard-reset", { method: "POST" }); toast("Engine startet neu"); } catch (e: any) { toast(e.message, true); } } }}>Harter Reset</button></div>
        {h?.problems?.length ? h.problems.map((p: any, i: number) => <div key={i} className={`banner ${p.level}`} style={{ marginTop: 8 }}>⚠ {p.text}</div>) : <div className="banner ok" style={{ marginTop: 8 }}>Keine Probleme.</div>}
        <div className="small muted">Engine {h?.engine?.state} · Version {h?.engine?.version} · läuft seit {h?.engine?.started_at ? fmtTime(h.engine.started_at) : "–"} · Speicher frei {h?.disk?.avail ? gb(h.disk.avail) : "–"}</div>
        {h?.recent?.length > 0 && <table className="list" style={{ marginTop: 8 }}><tbody>{h.recent.map((r: any, i: number) => <tr key={i}><td className="small muted">{fmtTime(r.at)}</td><td>{r.type}</td><td className="small">{r.detail?.error ?? r.detail?.name ?? ""}</td></tr>)}</tbody></table>}
      </div>
      <div className="card">
        <div className="row"><h2 style={{ margin: 0 }}>Protokoll (letzte 200 Zeilen)</h2><span className="spacer" />
          <select style={{ width: 140 }} value={unit} onChange={(e) => setUnit(e.target.value)}><option value="engine">Engine</option><option value="api">API</option></select>
          <button className="btn small" onClick={reloadL}>Neu laden</button></div>
        <pre className="mono" style={{ background: "#10241d", color: "#d5e2dc", padding: 10, borderRadius: 8, maxHeight: 360, overflow: "auto", whiteSpace: "pre-wrap" }}>{logs?.text || "–"}</pre>
      </div>
      <div className="grid g2">
        <div className="card">
          <h2>Speicher</h2>
          <table className="list"><tbody>{(storage?.dirs ?? []).map((d: any) => <tr key={d.dir}><td>{d.label}</td><td className="small muted">{d.files} Dateien</td><td className="small">{gb(d.bytes)}</td></tr>)}
            <tr><td>Datenbank</td><td /><td className="small">{storage ? gb(storage.database_bytes) : "–"}</td></tr></tbody></table>
        </div>
        <div className="card">
          <h2>Reparaturen</h2>
          <p className="small muted" style={{ marginTop: 0 }}>Jede Reparatur kann gefahrlos mehrfach laufen.</p>
          <div className="stack">
            <button className="btn" onClick={() => repair("reanalyze_missing", "Analyse")}>Songs ohne Analyse neu analysieren</button>
            <button className="btn" onClick={() => repair("missing_files", "Dateien")}>Fehlende Dateien markieren</button>
            <button className="btn" onClick={() => repair("stale_log", "Sendeplan")}>Alte, nicht gesendete Sendeplan-Einträge als verpasst markieren</button>
            <button className="btn" onClick={() => repair("reset_sent", "Engine-Warteschlange")}>Engine-Warteschlange neu ausgeben</button>
          </div>
        </div>
      </div>
      <div className="grid g2">
        <div className="card">
          <div className="row"><h2 style={{ margin: 0 }}>Sicherungen</h2><span className="spacer" /><button className="btn small" onClick={async () => { try { const r = await api("/api/admin/backups", { method: "POST" }); toast(`Sicherung ${r.name} erstellt`); reloadB(); } catch (e: any) { toast(e.message, true); } }}>Jetzt sichern</button></div>
          <p className="small muted">Zusätzlich sichert der Server jede Nacht um 3:30 Uhr automatisch.</p>
          {(backups ?? []).map((b) => <div key={b.name} className="row small"><a href={`/api/admin/backups/${b.name}`}>{b.name}</a><span className="muted">{gb(b.bytes)}</span></div>)}
        </div>
        <div className="card">
          <h2>Angemeldete Geräte</h2>
          {(sessions ?? []).map((s) => (
            <div key={s.id} className="row small" style={{ borderBottom: "1px solid var(--line)", padding: "3px 0" }}>
              <b>{s.name}</b><span className="muted" title={s.user_agent}>{s.user_agent.slice(0, 40)}</span><span className="muted">{fmtTime(s.last_seen)}</span><span className="spacer" />
              <button className="btn small danger" onClick={async () => { await api(`/api/admin/sessions/${s.id}`, { method: "DELETE" }); reloadS(); }}>Abmelden</button>
            </div>
          ))}
        </div>
      </div>
      <MailSettings />
      <Branding />
      <div className="card">
        <h2>Datenbank-Konsole (nur lesen)</h2>
        <Field label="SELECT-Abfrage" full><textarea rows={3} className="mono" value={sql} onChange={(e) => setSql(e.target.value)} /></Field>
        <div className="row" style={{ marginTop: 6 }}><span className="spacer" /><button className="btn" onClick={async () => { try { setRes(await api("/api/admin/sql", { body: { sql } })); } catch (e: any) { toast(e.message, true); } }}>Ausführen</button></div>
        {res && (
          <div style={{ overflowX: "auto", marginTop: 8 }}>
            <table className="list"><thead><tr>{res.columns.map((c: string) => <th key={c}>{c}</th>)}</tr></thead>
              <tbody>{res.rows.map((r: any, i: number) => <tr key={i}>{res.columns.map((c: string) => <td key={c} className="small">{typeof r[c] === "object" && r[c] !== null ? JSON.stringify(r[c]) : String(r[c] ?? "")}</td>)}</tr>)}</tbody></table>
            <div className="small muted">{res.rows.length} Zeilen (höchstens 500)</div>
          </div>
        )}
      </div>
    </div>
  );
}

function MailSettings() {
  const { data: s, reload } = useLoad<any>("/api/admin/smtp");
  const [pw, setPw] = useState("");
  const [to, setTo] = useState("");
  if (!s) return null;
  const put = async (b: any) => { try { await api("/api/admin/smtp", { method: "PUT", body: b }); reload(); } catch (e: any) { toast(e.message, true); } };
  return (
    <div className="card">
      <h2>E-Mail-Versand (für Einladungen)</h2>
      <div className="form">
        <Field label="SMTP-Server"><input type="text" defaultValue={s.host} placeholder="z. B. smtp.example.de" onBlur={(e) => put({ host: e.target.value })} /></Field>
        <Field label="Port"><input type="number" defaultValue={s.port} onBlur={(e) => put({ port: Number(e.target.value) })} /></Field>
        <Field label="Verschlüsselung"><select value={s.secure ? "tls" : "starttls"} onChange={(e) => put({ secure: e.target.value === "tls" })}><option value="starttls">STARTTLS (meist Port 587)</option><option value="tls">TLS (meist Port 465)</option></select></Field>
        <Field label="Benutzer"><input type="text" defaultValue={s.username} onBlur={(e) => put({ username: e.target.value })} /></Field>
        <Field label="Passwort" hint={s.password_set ? "Gesetzt. Leer lassen, um es zu behalten." : "Wird nie wieder angezeigt."}><input type="password" autoComplete="new-password" value={pw} placeholder={s.password_set ? "•••••••• (gesetzt)" : ""} onChange={(e) => setPw(e.target.value)} onBlur={() => { if (pw) { put({ password: pw }); setPw(""); } }} /></Field>
        <Field label="Absender"><input type="text" defaultValue={s.from_addr} placeholder="Radiocast <radio@example.de>" onBlur={(e) => put({ from_addr: e.target.value })} /></Field>
        <Field label="Adresse der Oberfläche" hint="für Links in Mails, z. B. https://sendewerk.music-intelligence.de"><input type="text" defaultValue={s.public_url} onBlur={(e) => put({ public_url: e.target.value })} /></Field>
      </div>
      <div className="row" style={{ marginTop: 8 }}>
        <input type="email" placeholder="Testmail an …" value={to} onChange={(e) => setTo(e.target.value)} style={{ maxWidth: 260 }} />
        <button className="btn small" disabled={!to} onClick={async () => { try { await api("/api/admin/smtp/test", { body: { to } }); toast("Testmail verschickt"); } catch (e: any) { toast(e.message, true); } }}>Testmail senden</button>
      </div>
    </div>
  );
}

function Branding() {
  const { data: b, reload } = useLoad<any>("/api/branding");
  if (!b) return null;
  const put = async (v: any) => { await api("/api/admin/branding", { method: "PUT", body: { ...b, ...v } }); reload(); window.dispatchEvent(new Event("rc-branding")); };
  return (
    <div className="card">
      <h2>Branding</h2>
      <div className="form">
        <Field label="Name in der Oberfläche" hint="leer = Radiocast"><input type="text" defaultValue={b.name ?? ""} onBlur={(e) => put({ name: e.target.value })} /></Field>
        <Field label="Akzentfarbe" hint="Knöpfe, Markierungen"><input type="color" value={b.accent || "#e8673c"} onChange={(e) => put({ accent: e.target.value })} /></Field>
      </div>
    </div>
  );
}
